import { useCallback, useEffect, useState } from 'react';
import { Link, Title, useNotify } from 'react-admin';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { dateTimeNoSeconds } from '../../shared/dateTime';
import { menuLabel } from '../menuConfig';
import { FeaturesLoading, useFeature, useFeatureState } from '../featureFlags/FeatureGate';
import { useHasPermission } from '../PermissionGate';
import {
  FEATURE_FTL_NOTES,
  changeFtlNoteStatus,
  fetchFtlNotesInbox,
  notifyFtlNotesChanged,
  statusActionLabel,
  FEATURE_FTL_DECISION_NOTIFICATIONS,
  toErrorMessage,
  type FtlNote,
  type FtlNotesInbox as Inbox,
  type InboxFilter,
} from './ftlNotesApi';
import { noteStatusColor, statusCommentText } from './FtlNotesSection';

/**
 * „Uwagi od FTL” — lista odbiorcy (makieta back office v2, widok CS „Uwagi
 * od FTL”, ścieżka FTL-05; 20.10.2026, przełącznik `ftlNotes`).
 *
 * Do czasu działu CS odbiorcą jest przyjęcie zleceń (uprawnienie „Przyjęcie
 * zleceń” — wartość robocza). Domyślnie otwarte uwagi, pilne na górze, potem
 * od najdłużej czekającej. Stan zmienia odbiorca z komentarzem — autor widzi
 * go przy uwadze na karcie zlecenia, a zmiana idzie do historii zlecenia.
 */

export const FEATURE_OFF_NOTE = 'Ta funkcja jest wyłączona w wersji aplikacji, z której korzystasz.';
export const NO_PERMISSION_NOTE =
  'Uwagi od FTL odbiera przyjęcie zleceń (CS i Administracja) — Twoje konto nie ma tego uprawnienia.';

export const INBOX_FILTERS: Array<{ id: InboxFilter; name: string }> = [
  { id: 'open', name: 'Otwarte (nowe i w toku)' },
  { id: 'new', name: 'Nowe' },
  { id: 'in_progress', name: 'W toku' },
  { id: 'resolved', name: 'Załatwione' },
  { id: 'all', name: 'Wszystkie' },
];

/** Filtry stanu — z „zakończoną” (21.10.2026, `ftlDecisionNotifications`). */
export function inboxFilters(ended: boolean): Array<{ id: InboxFilter; name: string }> {
  return ended
    ? INBOX_FILTERS.map((f) => (f.id === 'resolved' ? { ...f, name: 'Zakończone' } : f))
    : INBOX_FILTERS;
}

/** „PL Trzemeszno → DE Hamburg” — kraj i miasto, czego nie ma, pomijamy. */
export function noteRouteText(order: FtlNote['order']): string {
  if (!order) return '—';
  const place = (country: string | null, city: string | null) =>
    [country, city].filter((x) => typeof x === 'string' && x.trim() !== '').join(' ') || '—';
  return `${place(order.fromCountry, order.fromCity)} → ${place(order.toCountry, order.toCity)}`;
}

export const FtlNotesInbox = () => {
  const on = useFeatureState(FEATURE_FTL_NOTES);
  const allowed = useHasPermission('ORDER_INTAKE');
  if (on === undefined) return <FeaturesLoading />;
  if (!on || !allowed) {
    return (
      <Box sx={{ p: 2 }}>
        <Title title={menuLabel('ftl-notes')} />
        <Alert severity="info">{on ? NO_PERMISSION_NOTE : FEATURE_OFF_NOTE}</Alert>
      </Box>
    );
  }
  return <InboxScreen />;
};

type Pending = { note: FtlNote; status: string };

const InboxScreen = () => {
  const notify = useNotify();
  // „Zakończona” zamiast „załatwiona” (21.10.2026, `ftlDecisionNotifications`).
  const ended = useFeature(FEATURE_FTL_DECISION_NOTIFICATIONS);
  const [filter, setFilter] = useState<InboxFilter>('open');
  const [inbox, setInbox] = useState<Inbox | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<Pending | null>(null);
  const [comment, setComment] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setInbox(await fetchFtlNotesInbox(filter));
      setError(null);
    } catch (e) {
      setError(toErrorMessage(e));
    }
  }, [filter]);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async () => {
    if (!pending) return;
    setSaving(true);
    try {
      await changeFtlNoteStatus(pending.note.id, pending.status, comment);
      notify('Stan uwagi zapisany', { type: 'success' });
      setPending(null);
      notifyFtlNotesChanged();
      await load();
    } catch (e) {
      notify(toErrorMessage(e), { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const statusLabel = (code: string) => inbox?.statuses.find((s) => s.code === code)?.label ?? code;

  return (
    <Box sx={{ p: 2 }} data-testid="ftl-notes-inbox">
      <Title title={menuLabel('ftl-notes')} />
      <Stack direction="row" spacing={2} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
        <Typography variant="h6" component="h1">
          Uwagi od FTL
        </Typography>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id="ftl-notes-filter">Stan</InputLabel>
          <Select
            labelId="ftl-notes-filter"
            label="Stan"
            value={filter}
            onChange={(e) => setFilter(e.target.value as InboxFilter)}
          >
            {inboxFilters(ended).map((f) => (
              <MenuItem key={f.id} value={f.id}>
                {f.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {inbox ? (
          <Typography variant="body2" color="text.secondary">
            {inbox.total > inbox.limit
              ? `Pokazano ${inbox.limit} z ${inbox.total}`
              : `Uwag: ${inbox.total}`}
          </Typography>
        ) : null}
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Uwagi planistów i Team Leaderów przy zleceniach. Zmień stan z komentarzem — autor zobaczy go przy uwadze na
        karcie zlecenia, a zmiana trafi do historii zlecenia.
      </Typography>
      {error ? <Alert severity="warning">{error}</Alert> : null}
      {inbox === null && !error ? <CircularProgress size={20} /> : null}
      {inbox && inbox.data.length === 0 ? (
        <Alert severity="info">Brak uwag w tym widoku.</Alert>
      ) : null}
      {inbox && inbox.data.length > 0 ? (
        <Table size="small" sx={{ '& td, & th': { px: 0.75, verticalAlign: 'top' } }}>
          <TableHead>
            <TableRow>
              <TableCell>Zgłoszono</TableCell>
              <TableCell>Zlecenie</TableCell>
              <TableCell>Od</TableCell>
              <TableCell>Treść</TableCell>
              <TableCell>Stan</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {inbox.data.map((n) => (
              <TableRow key={n.id} data-testid="ftl-notes-row">
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{dateTimeNoSeconds(n.createdAt)}</TableCell>
                <TableCell sx={{ minWidth: 150 }}>
                  {n.order ? (
                    <Link to={`/user-offers/${n.order.id}/show`}>{n.order.number ?? `#${n.order.id}`}</Link>
                  ) : (
                    `#${n.userOfferId}`
                  )}
                  <Typography variant="caption" color="text.secondary" component="p">
                    {noteRouteText(n.order)}
                  </Typography>
                </TableCell>
                <TableCell>{n.createdBy?.name ?? '—'}</TableCell>
                <TableCell sx={{ maxWidth: 420 }}>
                  <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap', mb: 0.5 }}>
                    <Chip size="small" variant="outlined" label={n.categoryLabel} />
                    {n.urgent ? <Chip size="small" color="error" variant="outlined" label="pilna" /> : null}
                  </Stack>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {n.content}
                  </Typography>
                </TableCell>
                <TableCell sx={{ maxWidth: 240 }}>
                  <Chip size="small" color={noteStatusColor(n.status)} label={n.statusLabel} />
                  {n.statusComment ? (
                    <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0.5 }}>
                      {statusCommentText(n)}
                    </Typography>
                  ) : null}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <Stack direction="row" spacing={0.5}>
                    {n.next.map((to) => (
                      <Button
                        key={to}
                        size="small"
                        variant={to === 'resolved' ? 'contained' : 'outlined'}
                        onClick={() => {
                          setComment('');
                          setPending({ note: n, status: to });
                        }}
                      >
                        {statusActionLabel(n.status, to, ended)}
                      </Button>
                    ))}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : null}

      <Dialog open={pending !== null} onClose={saving ? undefined : () => setPending(null)} fullWidth maxWidth="sm">
        <DialogTitle>
          {pending ? `Uwaga przy ${pending.note.order?.number ?? `#${pending.note.userOfferId}`}: ${statusLabel(pending.status)}` : ''}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Napisz, co zrobiono — komentarz zobaczy autor uwagi, a zmiana trafi do historii zlecenia.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={2}
            label="Komentarz"
            value={comment}
            disabled={saving}
            onChange={(e) => setComment(e.target.value)}
            slotProps={{ htmlInput: { maxLength: 2000, 'data-testid': 'ftl-note-comment' } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPending(null)} disabled={saving}>
            Anuluj
          </Button>
          <Button variant="contained" onClick={() => void save()} disabled={saving || comment.trim() === ''}>
            Zapisz stan
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
